import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../config';
import SharedNav from './SharedNav';
import StatusLabel from './shared/StatusLabel';
import './Logs.css';

function Logs({ user, onLogout }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('client-activity');
  const [clientLogs, setClientLogs] = useState([]);
  const [userSessions, setUserSessions] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    userId: '',
    action: ''
  });

  useEffect(() => {
    fetchAllData();
    // Refresh online users every 30 seconds
    const interval = setInterval(fetchOnlineUsers, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([
      fetchClientLogs(),
      fetchUserSessions(),
      fetchOnlineUsers()
    ]);
    setLoading(false);
  };

  const fetchClientLogs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/logs/client-activity?limit=500`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientLogs(response.data);
    } catch (err) {
      console.error('Error fetching client logs:', err);
    }
  };

  const fetchUserSessions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/logs/user-sessions?limit=500`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserSessions(response.data);
    } catch (err) {
      console.error('Error fetching user sessions:', err);
    }
  };

  const fetchOnlineUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/logs/online-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOnlineUsers(response.data);
    } catch (err) {
      console.error('Error fetching online users:', err);
    }
  };

  const formatDuration = (minutes) => {
    if (!minutes) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const filteredClientLogs = clientLogs.filter(log => {
    const matchesSearch = !filters.search ||
      log.client_name?.toLowerCase().includes(filters.search.toLowerCase()) ||
      log.user_email?.toLowerCase().includes(filters.search.toLowerCase());
    const matchesAction = !filters.action || log.action === filters.action;
    return matchesSearch && matchesAction;
  });

  const filteredUserSessions = userSessions.filter(session => {
    const matchesSearch = !filters.search ||
      session.user_email?.toLowerCase().includes(filters.search.toLowerCase()) ||
      session.city?.toLowerCase().includes(filters.search.toLowerCase());
    const matchesUser = !filters.userId || session.user_id === parseInt(filters.userId);
    return matchesSearch && matchesUser;
  });

  const getUniqueUsers = () => {
    const users = [...new Set(userSessions.map(s => JSON.stringify({ id: s.user_id, email: s.user_email })))];
    return users.map(u => JSON.parse(u));
  };

  return (
    <div className="logs-page">
      <SharedNav user={user} onLogout={onLogout} />

      <div className="dashboard-content">
        <div className="logs-header">
          <div className="online-status" style={{ marginLeft: 'auto' }}>
            <span className="online-indicator"></span>
            <span className="online-count">{onlineUsers.length} Users Online</span>
          </div>
        </div>

        <div className="tabs">
          <button
            className={`tab ${activeTab === 'client-activity' ? 'active' : ''}`}
            onClick={() => setActiveTab('client-activity')}
          >
            Client Activity ({filteredClientLogs.length})
          </button>
          <button
            className={`tab ${activeTab === 'user-sessions' ? 'active' : ''}`}
            onClick={() => setActiveTab('user-sessions')}
          >
            User Sessions ({filteredUserSessions.length})
          </button>
          <button
            className={`tab ${activeTab === 'online-users' ? 'active' : ''}`}
            onClick={() => setActiveTab('online-users')}
          >
            Currently Online ({onlineUsers.length})
          </button>
        </div>

        <div className="filters">
          <input
            type="text"
            className="search-box"
            placeholder="Search by name, email, location..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />

          {activeTab === 'client-activity' && (
            <select
              value={filters.action}
              onChange={(e) => setFilters({ ...filters, action: e.target.value })}
              className="filter-select"
            >
              <option value="">All Actions</option>
              <option value="created">Created</option>
              <option value="updated">Updated</option>
              <option value="deleted">Deleted</option>
            </select>
          )}

          {activeTab === 'user-sessions' && (
            <select
              value={filters.userId}
              onChange={(e) => setFilters({ ...filters, userId: e.target.value })}
              className="filter-select"
            >
              <option value="">All Users</option>
              {getUniqueUsers().map(u => (
                <option key={u.id} value={u.id}>{u.email}</option>
              ))}
            </select>
          )}
        </div>

        {loading ? (
          <div className="loading">Loading logs...</div>
        ) : (
          <>
            {activeTab === 'client-activity' && (
              <div className="logs-table-container mtn-table-shell">
              <div className="mtn-scroll-x">
                <table className="logs-table">
                  <thead>
                    <tr>
                      <th>Date & Time (IST)</th>
                      <th>Client Name</th>
                      <th>Action</th>
                      <th>User</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredClientLogs.map(log => (
                      <tr key={log.id}>
                        <td className="timestamp">{log.timestamp_ist}</td>
                        <td className="client-name">{log.client_name}</td>
                        <td>
                          <span className={`action-badge action-${log.action}`}>
                            {log.action}
                          </span>
                        </td>
                        <td>{log.user_email}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
                {filteredClientLogs.length === 0 && (
                  <div className="empty-state">No activity logs found</div>
                )}
              </div>
            )}

            {activeTab === 'user-sessions' && (
              <div className="logs-table-container mtn-table-shell">
              <div className="mtn-scroll-x">
                <table className="logs-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Login Time (IST)</th>
                      <th>Logout Time (IST)</th>
                      <th>Duration</th>
                      <th>IP Address</th>
                      <th>Location</th>
                      <th>End Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUserSessions.map(session => (
                      <tr key={session.id} className={session.is_active ? 'active-session' : ''}>
                        <td>{session.user_email}</td>
                        <td className="timestamp">{session.login_time_ist}</td>
                        <td className="timestamp">
                          {session.logout_time_ist || (session.is_active ? 'Online' : 'N/A')}
                        </td>
                        <td>{formatDuration(session.session_duration_minutes)}</td>
                        <td className="ip-address">{session.ip_address}</td>
                        <td>
                          {session.city}, {session.country}
                        </td>
                        <td>
                          {session.is_active ? (
                            <StatusLabel value="Active" tier="pass" />
                          ) : (
                            <StatusLabel value={session.end_type || 'N/A'} tier="neutral" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
                {filteredUserSessions.length === 0 && (
                  <div className="empty-state">No user sessions found</div>
                )}
              </div>
            )}

            {activeTab === 'online-users' && (
              <div className="online-users-grid">
                {onlineUsers.length === 0 ? (
                  <div className="empty-state">No users currently online</div>
                ) : (
                  onlineUsers.map((user, idx) => (
                    <div key={idx} className="online-user-card">
                      <div className="card-header">
                        <span className="online-dot"></span>
                        <strong>{user.user_email}</strong>
                      </div>
                      <div className="card-body">
                        <div className="info-row">
                          <span className="label">Login Time:</span>
                          <span className="value">{user.login_time_ist}</span>
                        </div>
                        <div className="info-row">
                          <span className="label">Last Activity:</span>
                          <span className="value">{user.last_activity_ist}</span>
                        </div>
                        <div className="info-row">
                          <span className="label">IP Address:</span>
                          <span className="value">{user.ip_address}</span>
                        </div>
                        <div className="info-row">
                          <span className="label">Location:</span>
                          <span className="value">{user.city}, {user.country}</span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Logs;
